import { useEffect, useMemo, useRef, useState } from "react";

import { Check, Search, X } from "lucide-react";
import { useDispatch } from "react-redux";

import { ReactComponent as LoadingIcon } from "@/assets/svg/loading-spinner.svg";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SelectionActionBar } from "@/components/ui/selection-action-bar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ExtractTargetField,
  ExtractType,
  useMapExtractToModules,
} from "@/hooks/mutations/use-module-extracts";
import { useTemplateById } from "@/hooks/queries/use-templates";
import { isAutoInsertedModule } from "@/pages/ReportStudio/ReportBuilder/constants";
import { setMessage } from "@/redux/notification";

type MapToReportBuilderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  text: string;
  targetField: ExtractTargetField;
  description?: string;
  pageNumber?: number;
  caseNumber?: string;
  templateId?: string;
  sourceFileName?: string;
  /** Originating Prepare/Scribe category slug (e.g. the assessment field a
   *  scribe highlight came from). Surfaced as note provenance in Report Builder. */
  categorySlug?: string;
  extractType?: ExtractType;
  onMapped?: () => void;
};

// Static legal-preamble fragments that aren't auto-inserted but also aren't
// valid snippet-mapping targets — the section is boilerplate the user fills
// at case creation, not from document evidence. Kept as a flat lowercased set
// to match either the standalone module names or the combined heading.
const EXTRA_HIDDEN_MODULES = new Set([
  "expectation setting",
  "service standards",
  "qualifications and suitability",
]);

const normalizeCase = (s: string) => {
  if (!s) return s;
  const isAllCaps = s === s.toUpperCase() && /[A-Z]/.test(s);
  if (!isAllCaps) return s;
  return s
    .toLowerCase()
    .replace(/(^|[\s/(-])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());
};

export const MapToReportBuilderDialog = ({
  open,
  onOpenChange,
  text,
  targetField,
  description = "Select one or more sections where this text should appear",
  pageNumber,
  caseNumber,
  templateId,
  sourceFileName,
  categorySlug,
  extractType = "snip",
  onMapped,
}: MapToReportBuilderDialogProps) => {
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  // Brief "Mapping Successful" confirmation on the primary button before the
  // dialog closes (MDRS-113 design). Cleared on close.
  const [justMapped, setJustMapped] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  // Holds the "Mapping Successful" close timer so it can be cancelled if the
  // dialog unmounts within the 900ms window (avoids stale side effects).
  const successTimerRef = useRef<number | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!open) {
      setSelectedModules([]);
      setQuery("");
      setJustMapped(false);
    }
  }, [open]);

  // Cancel any pending success-close timer on unmount.
  useEffect(
    () => () => {
      if (successTimerRef.current !== null) {
        window.clearTimeout(successTimerRef.current);
      }
    },
    []
  );

  const { data: template, isLoading: isLoadingTemplate } = useTemplateById(
    templateId,
    { enabled: open && !!templateId }
  );

  const { mutate: mapExtract, isPending: isSubmitting } = useMapExtractToModules();

  const sections = useMemo(() => {
    if (!template?.sections) return [];
    const q = query.trim().toLowerCase();
    return [...template.sections]
      .sort((a, b) => a.order - b.order)
      .map((section) => ({
        ...section,
        modules: [...section.modules]
          .sort((a, b) => a.order - b.order)
          .filter(
            (mod) =>
              !isAutoInsertedModule(mod.name) &&
              !EXTRA_HIDDEN_MODULES.has(mod.name.toLowerCase())
          )
          // Note: we intentionally do NOT gate on doc_sorter/scribe extract
          // slugs. Those flag which modules auto-populate from a source, but any
          // heading is a valid *manual* map target — the map mutation keys on
          // module_id alone. Requiring a slug hid every section of the
          // headings-only service-type templates (MDRS-118), e.g. Medical
          // Negligence, producing "No sections available" (MDRS-143).
          .filter((mod) => !q || mod.name.toLowerCase().includes(q)),
      }))
      .filter((section) => section.modules.length > 0);
  }, [template, query]);

  // Notes map into the *section* (MDRS-143 follow-up): subsections (child
  // modules) are no longer individually selectable. Toggling a section
  // adds/removes all of its module ids at once — sections are single-module in
  // practice, so this is a 1:1 map with a safe multi-id fallback.
  const handleToggleSection = (sectionModuleIds: string[]) => {
    const allSelected = sectionModuleIds.every((id) => selectedModules.includes(id));
    setSelectedModules((prev) =>
      allSelected
        ? prev.filter((id) => !sectionModuleIds.includes(id))
        : Array.from(new Set([...prev, ...sectionModuleIds]))
    );
  };

  const handleClearSelection = () => setSelectedModules([]);

  // Count of selected *sections* (not modules) — a section is selected when all
  // its module ids are in the selection.
  const selectedSectionCount = sections.filter(
    (section) =>
      section.modules.length > 0 &&
      section.modules.every((m) => selectedModules.includes(m.id))
  ).length;

  const handleSubmit = () => {
    if (!caseNumber || selectedModules.length === 0) return;

    mapExtract(
      {
        case_number: caseNumber,
        module_ids: selectedModules,
        text,
        page_number: pageNumber,
        source_file_name: sourceFileName,
        category_slug: categorySlug,
        extract_type: extractType,
        target_field: targetField,
      },
      {
        onSuccess: () => {
          // Flash "Mapping Successful" on the button, then close so the
          // confirmation is visible before the dialog dismisses.
          setJustMapped(true);
          const mappedCount = selectedSectionCount;
          successTimerRef.current = window.setTimeout(() => {
            successTimerRef.current = null;
            setSelectedModules([]);
            onOpenChange(false);
            dispatch(
              setMessage({
                content: `Mapped to ${mappedCount} section${
                  mappedCount !== 1 ? "s" : ""
                } in Report Builder`,
                type: "success",
              })
            );
            onMapped?.();
          }, 900);
        },
        onError: () => {
          dispatch(
            setMessage({
              content: "Failed to map to Report Builder",
              type: "error",
            })
          );
        },
      }
    );
  };

  const handleCancel = () => onOpenChange(false);

  const count = selectedSectionCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[min(85vh,720px)] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchRef.current?.focus();
        }}
        onKeyDown={(e) => {
          if (
            e.key === "Enter" &&
            !e.shiftKey &&
            count > 0 &&
            !isSubmitting &&
            (e.target as HTMLElement).tagName !== "INPUT" &&
            (e.target as HTMLElement).tagName !== "TEXTAREA"
          ) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <DialogHeader className="flex-shrink-0 border-b px-6 pb-4 pt-6">
          <DialogTitle>Map to Report Builder</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 px-6 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <Card className="min-h-0 gap-0 overflow-hidden py-0">
            <CardHeader className="flex flex-shrink-0 flex-row items-center gap-2 border-b py-4">
              <CardTitle className="text-sm">Selected Text</CardTitle>
              {pageNumber && (
                <Badge variant="default" className="px-2 py-0 text-[10px]">
                  Page {pageNumber}
                </Badge>
              )}
            </CardHeader>
            <CardContent className="min-h-0 flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full bg-primary-50/30">
                <div className="px-6 py-4">
                  <p className="whitespace-pre-wrap text-sm italic leading-relaxed text-foreground">
                    {text}
                  </p>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card className="min-h-0 gap-0 overflow-hidden py-0">
            <CardHeader className="flex flex-shrink-0 flex-row items-center justify-between gap-3 border-b py-4">
              <CardTitle className="text-sm">Report Builder Sections</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col gap-2 px-6 py-4">
              <div className="relative flex-shrink-0">
                <Input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search sections"
                  aria-label="Search sections"
                  leftIcon={<Search className="h-4 w-4" />}
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="text-muted-foreground hover:bg-muted absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-0.5 hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <SelectionActionBar
                count={count}
                onClear={handleClearSelection}
                disabled={isSubmitting}
                className="flex-shrink-0"
              />

              {isLoadingTemplate && (
                <div className="text-muted-foreground flex flex-1 items-center justify-center gap-2 text-sm">
                  <LoadingIcon className="h-4 w-4 animate-spin-ccw" />
                  Loading sections...
                </div>
              )}

              {!isLoadingTemplate && sections.length === 0 && (
                <div className="text-muted-foreground flex flex-1 items-center justify-center px-4 text-center text-sm">
                  {query ? `No sections match "${query}"` : "No sections available"}
                </div>
              )}

              {!isLoadingTemplate && sections.length > 0 && (
                <ScrollArea className="min-h-0 flex-1 rounded-md border [&>[data-slot=scroll-area-viewport]>div]:!block">
                  <div className="divide-y">
                    {sections.map((section) => {
                      const sectionIds = section.modules.map((m) => m.id);
                      const checked = sectionIds.every((id) =>
                        selectedModules.includes(id)
                      );
                      return (
                        <label
                          key={section.id}
                          className={`flex min-w-0 cursor-pointer items-start gap-3 border-b px-4 py-3 text-sm transition-colors ${
                            checked
                              ? "bg-primary-100 hover:bg-primary-100/80"
                              : "hover:bg-muted/50"
                          } ${isSubmitting ? "pointer-events-none opacity-60" : ""}`}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={isSubmitting}
                            onCheckedChange={() => handleToggleSection(sectionIds)}
                            className="mt-0.5 flex-shrink-0"
                          />
                          <span className="min-w-0 break-words font-medium text-foreground">
                            {normalizeCase(section.name)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t bg-background-white px-6 py-4">
          <Button
            variant="simple-outline"
            size="sm"
            onClick={handleCancel}
            disabled={isSubmitting || justMapped}
          >
            Cancel
          </Button>
          {(() => {
            const disabledReason = !caseNumber
              ? "Case number is required"
              : count === 0
                ? "Select at least one Section"
                : null;
            const isDisabled = !!disabledReason || isSubmitting || justMapped;
            const submitButton = (
              <Button
                size="sm"
                disabled={isDisabled}
                isLoading={isSubmitting}
                onClick={handleSubmit}
              >
                {justMapped ? (
                  <>
                    Mapping Successful
                    <Check className="size-4" />
                  </>
                ) : isSubmitting ? (
                  "Mapping"
                ) : (
                  <>
                    Map to {count > 0 ? `${count} ` : ""}Section
                    {count !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            );
            if (disabledReason && !isSubmitting && !justMapped) {
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <span tabIndex={0}>{submitButton}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{disabledReason}</TooltipContent>
                </Tooltip>
              );
            }
            return submitButton;
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
